import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Package,
  AlertCircle,
  Calendar
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../lib/api';

const Finance = () => {
  const [periodDays, setPeriodDays] = useState(30);
  const [customDates, setCustomDates] = useState({ start: '', end: '' });
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // États pour les données
  const [summary, setSummary] = useState(null);
  const [trendsTarif, setTrendsTarif] = useState([]);
  const [trendsImmob, setTrendsImmob] = useState([]);
  const [byTransporter, setByTransporter] = useState([]);
  const [byLane, setByLane] = useState([]);
  const [analyticsTable, setAnalyticsTable] = useState([]);
  
  // Nouveaux états pour les nouveaux onglets KPI
  const [costPerTon, setCostPerTon] = useState({ operations: [], statistics: {} });
  const [costPerVehicleType, setCostPerVehicleType] = useState({ vehicle_types: [] });
  const [costPerDestination, setCostPerDestination] = useState({ destinations: [] });
  const [costPerKm, setCostPerKm] = useState({ vehicle_costs: [] });
  const [driverPerformance, setDriverPerformance] = useState({ drivers: [] });
  const [tripComparison, setTripComparison] = useState({ trips: [], driver_comparison: [], best_driver: null });
  
  // États pour les filtres avancés
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filters, setFilters] = useState({
    type_camion: '',
    destination: '',
    chauffeur: '',
    origin: ''
  });

  // Charger toutes les données
  const fetchAllData = async (days, startDate = null, endDate = null) => {
    setLoading(true);
    try {
      // Construire les paramètres de requête
      const buildParams = () => {
        if (startDate && endDate) {
          return `start_date=${startDate}&end_date=${endDate}`;
        }
        return `period_days=${days}`;
      };
      
      const params = buildParams();
      
      // Utiliser Promise.allSettled pour les données principales
      const results = await Promise.allSettled([
        api.get(`/finance/summary?${params}`),
        api.get(`/finance/trends?${params}&metric=tarif`),
        api.get(`/finance/trends?${params}&metric=immobilisation`),
        api.get(`/finance/by-transporter?${params}`),
        api.get(`/finance/by-lane?${params}`),
        api.get(`/finance/analytics-table?${params}`)
      ]);

      // Traiter chaque résultat avec des valeurs par défaut
      setSummary(results[0].status === 'fulfilled' ? results[0].value.data : null);
      setTrendsTarif(results[1].status === 'fulfilled' ? (results[1].value.data.trends || []) : []);
      setTrendsImmob(results[2].status === 'fulfilled' ? (results[2].value.data.trends || []) : []);
      setByTransporter(results[3].status === 'fulfilled' ? (results[3].value.data.transporters || []) : []);
      setByLane(results[4].status === 'fulfilled' ? (results[4].value.data.lanes || []) : []);
      setAnalyticsTable(results[5].status === 'fulfilled' ? (results[5].value.data.analytics || []) : []);
      
      // Logger les erreurs
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Erreur endpoint finance #${index}:`, result.reason);
        }
      });
      
      // Charger TOUS les nouveaux KPIs pour que tous les onglets soient à jour
      await fetchAllNewKPIs(params);
    } catch (error) {
      console.error('Erreur chargement données finance:', error);
    } finally {
      setLoading(false);
    }
  };

  // Charger les données spécifiques à chaque onglet
  const fetchTabData = async (tab, params) => {
    try {
      switch (tab) {
        case 'cost-per-ton':
          const costTonRes = await api.get(`/finance/cost-per-ton?${params}`);
          setCostPerTon(costTonRes.data);
          break;
        case 'cost-per-vehicle':
          const costVehicleRes = await api.get(`/finance/cost-per-vehicle-type?${params}`);
          setCostPerVehicleType(costVehicleRes.data);
          break;
        case 'cost-per-destination':
          const costDestRes = await api.get(`/finance/cost-per-destination?${params}`);
          setCostPerDestination(costDestRes.data);
          break;
        case 'cost-per-km':
          const costKmRes = await api.get(`/finance/cost-per-km?${params}`);
          setCostPerKm(costKmRes.data);
          break;
        case 'driver-performance':
          const driverRes = await api.get(`/finance/driver-performance?${params}`);
          setDriverPerformance(driverRes.data);
          break;
        case 'trip-comparison':
          const tripRes = await api.get(`/finance/trip-comparison?${params}`);
          setTripComparison(tripRes.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Erreur chargement données ${tab}:`, error);
    }
  };

  // Charger tous les nouveaux onglets KPI en une fois
  const fetchAllNewKPIs = async (params) => {
    try {
      // Utiliser Promise.allSettled pour que les erreurs d'un endpoint n'affectent pas les autres
      const results = await Promise.allSettled([
        api.get(`/finance/cost-per-ton?${params}`),
        api.get(`/finance/cost-per-vehicle-type?${params}`),
        api.get(`/finance/cost-per-destination?${params}`),
        api.get(`/finance/cost-per-km?${params}`),
        api.get(`/finance/driver-performance?${params}`),
        api.get(`/finance/trip-comparison?${params}`)
      ]);

      // Traiter chaque résultat individuellement
      if (results[0].status === 'fulfilled') {
        setCostPerTon(results[0].value.data);
      } else {
        console.error('Erreur cost-per-ton:', results[0].reason);
        setCostPerTon({ operations: [], statistics: {} });
      }

      if (results[1].status === 'fulfilled') {
        setCostPerVehicleType(results[1].value.data);
      } else {
        console.error('Erreur cost-per-vehicle:', results[1].reason);
        setCostPerVehicleType({ vehicle_types: [] });
      }

      if (results[2].status === 'fulfilled') {
        setCostPerDestination(results[2].value.data);
      } else {
        console.error('Erreur cost-per-destination:', results[2].reason);
        setCostPerDestination({ destinations: [] });
      }

      if (results[3].status === 'fulfilled') {
        setCostPerKm(results[3].value.data);
      } else {
        console.error('Erreur cost-per-km:', results[3].reason);
        setCostPerKm({ vehicle_costs: [] });
      }

      if (results[4].status === 'fulfilled') {
        setDriverPerformance(results[4].value.data);
      } else {
        console.error('Erreur driver-performance:', results[4].reason);
        setDriverPerformance({ drivers: [] });
      }

      if (results[5].status === 'fulfilled') {
        setTripComparison(results[5].value.data);
      } else {
        console.error('Erreur trip-comparison:', results[5].reason);
        setTripComparison({ trips: [], driver_comparison: [], best_driver: null });
      }
    } catch (error) {
      console.error('Erreur chargement nouveaux KPIs:', error);
    }
  };

  useEffect(() => {
    if (useCustomDates && customDates.start && customDates.end) {
      fetchAllData(null, customDates.start, customDates.end);
    } else {
      fetchAllData(periodDays);
    }
  }, [periodDays, customDates, useCustomDates]);
  
  // Handler pour changement de période rapide
  const handleQuickPeriod = (days) => {
    setUseCustomDates(false);
    setPeriodDays(days);
  };
  
  // Handler pour dates personnalisées
  const handleCustomDates = () => {
    if (customDates.start && customDates.end) {
      setUseCustomDates(true);
      fetchAllData(null, customDates.start, customDates.end);
    }
  };

  // Formater montant en FCFA
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // KPI Card Component
  const KPICard = ({ title, value, icon: Icon, trend, color = "emerald" }) => (
    <Card className={`border-l-4 border-${color}-500`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold mt-2">{value}</p>
            {trend && (
              <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? <TrendingUp className="inline h-3 w-3" /> : <TrendingDown className="inline h-3 w-3" />}
                {' '}{Math.abs(trend)}% vs période précédente
              </p>
            )}
          </div>
          <div className={`p-3 bg-${color}-100 rounded-full`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données financières...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tableau de Bord Financier</h1>
          <p className="text-gray-600 mt-1">Analyse des coûts et performances financières</p>
        </div>
        
        {/* Filtres période */}
        <div className="flex flex-col gap-3">
          {/* Périodes rapides */}
          <div className="flex gap-2 flex-wrap">
            {[7, 30, 90, 180, 365, 730].map((days) => {
              const label = days === 7 ? '7j' : 
                           days === 30 ? '1m' : 
                           days === 90 ? '3m' : 
                           days === 180 ? '6m' : 
                           days === 365 ? '1an' : '2ans';
              return (
                <Button
                  key={days}
                  size="sm"
                  variant={!useCustomDates && periodDays === days ? "default" : "outline"}
                  onClick={() => handleQuickPeriod(days)}
                  className={!useCustomDates && periodDays === days ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                >
                  <Calendar className="h-3 w-3 mr-1" />
                  {label}
                </Button>
              );
            })}
          </div>
          
          {/* Dates personnalisées */}
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={customDates.start}
              onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm"
              placeholder="Date début"
            />
            <span className="text-gray-500">→</span>
            <input
              type="date"
              value={customDates.end}
              onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm"
              placeholder="Date fin"
            />
            <Button
              size="sm"
              onClick={handleCustomDates}
              disabled={!customDates.start || !customDates.end}
              className={useCustomDates ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            >
              Appliquer
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <KPICard
          title="Montant Total Versé"
          value={formatCurrency(summary?.total_tarif_transporteur)}
          icon={DollarSign}
          color="emerald"
        />
        <KPICard
          title="Coût Total Immobilisation"
          value={formatCurrency(summary?.total_cout_immobilisation)}
          icon={Clock}
          color="orange"
        />
        <KPICard
          title="Coût Moyen par KM"
          value={formatCurrency(summary?.avg_cout_km)}
          icon={TrendingUp}
          color="blue"
        />
        <KPICard
          title="Coût Moyen par Tonne"
          value={formatCurrency(summary?.avg_cout_tonne)}
          icon={Package}
          color="purple"
        />
        <KPICard
          title="% Opérations Immobilisées"
          value={`${summary?.pct_operations_immobilisation || 0}%`}
          icon={AlertCircle}
          color="red"
        />
        <KPICard
          title="Immobilisation Moyenne"
          value={formatCurrency(summary?.avg_immobilisation_par_operation)}
          icon={Clock}
          color="amber"
        />
      </div>

      {/* Tabs pour graphiques */}
      <Tabs 
        defaultValue="dashboard" 
        className="space-y-4"
        onValueChange={(value) => setActiveTab(value)}
      >
        <div className="overflow-x-auto">
          <TabsList className="inline-flex w-auto min-w-full">
            <TabsTrigger value="dashboard">📊 Tableau de Bord</TabsTrigger>
            <TabsTrigger value="transporters">🚚 Transporteurs</TabsTrigger>
            <TabsTrigger value="lanes">🛣️ Trajets</TabsTrigger>
            <TabsTrigger value="analytics">📋 Analyse Détaillée</TabsTrigger>
            <TabsTrigger value="cost-per-ton">⚖️ Coût/Tonne</TabsTrigger>
            <TabsTrigger value="cost-per-vehicle">🚛 Coût/Véhicule</TabsTrigger>
            <TabsTrigger value="cost-per-destination">📍 Coût/Destination</TabsTrigger>
            <TabsTrigger value="cost-per-km">📏 Coût/KM</TabsTrigger>
            <TabsTrigger value="driver-performance">👤 Performance Chauffeurs</TabsTrigger>
            <TabsTrigger value="trip-comparison">🔄 Comparaison Trajets</TabsTrigger>
          </TabsList>
        </div>

        {/* Onglet 1: Tableau de Bord Transport */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Graphique tendance tarifs */}
            <Card>
              <CardHeader>
                <CardTitle>Évolution Montants Versés</CardTitle>
                <CardDescription>
                  {useCustomDates && customDates.start && customDates.end
                    ? `Du ${new Date(customDates.start).toLocaleDateString('fr-FR')} au ${new Date(customDates.end).toLocaleDateString('fr-FR')}`
                    : `Tendance journalière sur ${periodDays} jours`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={trendsTarif}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" fontSize={10} />
                    <YAxis fontSize={10} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelStyle={{ color: '#000' }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      name="Montant Total"
                      dot={{ fill: '#10b981' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                {/* Interprétation dynamique */}
                {trendsTarif.length > 0 && (() => {
                  const values = trendsTarif.map(d => d.total || 0);
                  const avg = values.reduce((a, b) => a + b, 0) / values.length;
                  const max = Math.max(...values);
                  const min = Math.min(...values);
                  const maxDate = trendsTarif[values.indexOf(max)]?.date;
                  const minDate = trendsTarif[values.indexOf(min)]?.date;
                  const lastValue = values[values.length - 1];
                  const firstValue = values[0];
                  const trend = lastValue > firstValue ? 'hausse' : lastValue < firstValue ? 'baisse' : 'stable';
                  const trendPercent = firstValue > 0 ? (((lastValue - firstValue) / firstValue) * 100).toFixed(1) : 0;
                  
                  return (
                    <div className="mt-3 p-3 bg-blue-50 rounded text-sm">
                      <p className="font-semibold text-blue-900 mb-1">📊 Analyse:</p>
                      <p className="text-blue-800">
                        {trend === 'hausse' ? '📈' : trend === 'baisse' ? '📉' : '➡️'} 
                        <strong> Tendance {trend}</strong> de {Math.abs(trendPercent)}% sur la période.
                        <br />
                        💰 Pic à <strong>{formatCurrency(max)}</strong> le {new Date(maxDate).toLocaleDateString('fr-FR')}.
                        <br />
                        📉 Creux à <strong>{formatCurrency(min)}</strong> le {new Date(minDate).toLocaleDateString('fr-FR')}.
                        <br />
                        📊 Moyenne: <strong>{formatCurrency(avg)}</strong>
                        {trend === 'hausse' && ' - Performance en amélioration ✅'}
                        {trend === 'baisse' && ' - Attention à la baisse ⚠️'}
                      </p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Graphique tendance immobilisation */}
            <Card>
              <CardHeader>
                <CardTitle>Évolution Coûts Immobilisation</CardTitle>
                <CardDescription>
                  {useCustomDates && customDates.start && customDates.end
                    ? `Du ${new Date(customDates.start).toLocaleDateString('fr-FR')} au ${new Date(customDates.end).toLocaleDateString('fr-FR')}`
                    : `Tendance journalière sur ${periodDays} jours`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={trendsImmob}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" fontSize={10} />
                    <YAxis fontSize={10} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelStyle={{ color: '#000' }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#f59e0b" 
                      fill="#fbbf24"
                      fillOpacity={0.3}
                      name="Coût Immobilisation"
                    />
                  </AreaChart>
                </ResponsiveContainer>
                {/* Interprétation dynamique */}
                {trendsImmob.length > 0 && (() => {
                  const values = trendsImmob.map(d => d.total || 0);
                  const nonZeroValues = values.filter(v => v > 0);
                  const totalDays = values.length;
                  const daysWithImmob = nonZeroValues.length;
                  const avg = nonZeroValues.length > 0 ? nonZeroValues.reduce((a, b) => a + b, 0) / nonZeroValues.length : 0;
                  const max = Math.max(...values);
                  const maxDate = max > 0 ? trendsImmob[values.indexOf(max)]?.date : null;
                  const immobRate = ((daysWithImmob / totalDays) * 100).toFixed(0);
                  
                  return (
                    <div className="mt-3 p-3 bg-amber-50 rounded text-sm">
                      <p className="font-semibold text-amber-900 mb-1">📊 Analyse:</p>
                      <p className="text-amber-800">
                        {daysWithImmob === 0 ? (
                          <>✅ <strong>Aucune immobilisation</strong> sur la période - Performance excellente!</>
                        ) : (
                          <>
                            ⏱️ Immobilisations sur <strong>{daysWithImmob}/{totalDays} jours</strong> ({immobRate}% du temps).
                            <br />
                            💰 Coût moyen/jour: <strong>{formatCurrency(avg)}</strong>
                            {max > 0 && (
                              <>
                                <br />
                                🚨 Pic à <strong>{formatCurrency(max)}</strong> le {new Date(maxDate).toLocaleDateString('fr-FR')}.
                              </>
                            )}
                            <br />
                            {immobRate > 30 && '⚠️ Taux d\'immobilisation élevé - Optimisation nécessaire'}
                            {immobRate <= 30 && immobRate > 10 && '⚡ Taux d\'immobilisation modéré'}
                            {immobRate <= 10 && '✅ Taux d\'immobilisation faible - Bon contrôle'}
                          </>
                        )}
                      </p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Onglet Transporteurs (RÉTABLI) */}
        <TabsContent value="transporters">
          <Card>
            <CardHeader>
              <CardTitle>Top 10 Transporteurs par Montant</CardTitle>
              <CardDescription>
                {useCustomDates && customDates.start && customDates.end
                  ? `Du ${new Date(customDates.start).toLocaleDateString('fr-FR')} au ${new Date(customDates.end).toLocaleDateString('fr-FR')}`
                  : `Répartition des montants versés sur ${periodDays} jours`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={byTransporter} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" fontSize={10} />
                  <YAxis dataKey="transporter_name" type="category" width={120} fontSize={10} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelStyle={{ color: '#000' }}
                  />
                  <Legend />
                  <Bar dataKey="total_tarif" fill="#10b981" name="Montant Versé" />
                  <Bar dataKey="total_immobilisation" fill="#f59e0b" name="Immobilisation" />
                </BarChart>
              </ResponsiveContainer>
              {/* Interprétation dynamique */}
              {byTransporter.length > 0 && (() => {
                const totalMontant = byTransporter.reduce((sum, t) => sum + (t.total_tarif || 0), 0);
                const top3 = byTransporter.slice(0, 3);
                const top3Total = top3.reduce((sum, t) => sum + (t.total_tarif || 0), 0);
                const top3Percent = ((top3Total / totalMontant) * 100).toFixed(0);
                const topTransporter = byTransporter[0];
                const topPercent = ((topTransporter.total_tarif / totalMontant) * 100).toFixed(0);
                
                return (
                  <div className="mt-3 p-3 bg-emerald-50 rounded text-sm">
                    <p className="font-semibold text-emerald-900 mb-1">📊 Analyse:</p>
                    <p className="text-emerald-800">
                      🏆 <strong>{topTransporter.transporter_name}</strong> est le leader avec {formatCurrency(topTransporter.total_tarif)} ({topPercent}% du total).
                      <br />
                      📈 Les 3 premiers représentent <strong>{top3Percent}%</strong> des montants versés.
                      <br />
                      {top3Percent > 70 && '⚠️ Forte concentration - Risque de dépendance élevé'}
                      {top3Percent <= 70 && top3Percent > 50 && '⚡ Concentration modérée - Portfolio équilibré'}
                      {top3Percent <= 50 && '✅ Bonne diversification des transporteurs'}
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Trajets (RÉTABLI) */}
        <TabsContent value="lanes">
          <Card>
            <CardHeader>
              <CardTitle>Top 15 Trajets par Montant</CardTitle>
              <CardDescription>
                {useCustomDates && customDates.start && customDates.end
                  ? `Du ${new Date(customDates.start).toLocaleDateString('fr-FR')} au ${new Date(customDates.end).toLocaleDateString('fr-FR')}`
                  : `Analyse par trajet sur ${periodDays} jours`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Trajet</th>
                      <th className="px-4 py-2 text-right">Montant Total</th>
                      <th className="px-4 py-2 text-right">Montant Moyen</th>
                      <th className="px-4 py-2 text-right">Immobilisation</th>
                      <th className="px-4 py-2 text-center">Opérations</th>
                      <th className="px-4 py-2 text-right">Distance Moy.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byLane.map((lane, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{lane.lane}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(lane.total_tarif)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(lane.avg_tarif)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(lane.total_immobilisation)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                            {lane.count_operations}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {lane.avg_distance_km ? `${lane.avg_distance_km} km` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Analyse Détaillée (RÉTABLI) */}
        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Tableau Analytique Détaillé</CardTitle>
              <CardDescription>Métriques par trajet, transporteur et type de marchandise</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Trajet</th>
                      <th className="px-4 py-2 text-left">Transporteur</th>
                      <th className="px-4 py-2 text-left">Marchandise</th>
                      <th className="px-4 py-2 text-right">Tarif Moy.</th>
                      <th className="px-4 py-2 text-right">€/km</th>
                      <th className="px-4 py-2 text-right">€/t</th>
                      <th className="px-4 py-2 text-right">% Immob.</th>
                      <th className="px-4 py-2 text-center">Opés</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsTable.map((row, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-xs">{row.lane}</td>
                        <td className="px-4 py-3 text-xs">{row.transporter}</td>
                        <td className="px-4 py-3 text-xs">{row.type_marchandise}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(row.avg_tarif)}</td>
                        <td className="px-4 py-3 text-right">
                          {row.avg_cout_km ? formatCurrency(row.avg_cout_km) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {row.avg_cout_tonne ? formatCurrency(row.avg_cout_tonne) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            row.pct_operations_immob > 50 ? 'bg-red-100 text-red-700' :
                            row.pct_operations_immob > 20 ? 'bg-orange-100 text-orange-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {row.pct_operations_immob}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {row.count_operations}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet 2: Coût par Tonne */}
        <TabsContent value="cost-per-ton" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Coût par Tonne (par Course)</CardTitle>
              <CardDescription>
                Analyse de l'efficacité de chaque trajet basée sur le chargement
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 grid grid-cols-3 gap-4">
                <Card className="bg-blue-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-600">Coût Moyen/Tonne</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(costPerTon.statistics?.avg_cout_par_tonne || 0)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-600">Minimum</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(costPerTon.statistics?.min_cout_par_tonne || 0)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-600">Maximum</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(costPerTon.statistics?.max_cout_par_tonne || 0)}
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Matricule</th>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Trajet</th>
                      <th className="px-4 py-2 text-right">Poids (t)</th>
                      <th className="px-4 py-2 text-right">Tarif Total</th>
                      <th className="px-4 py-2 text-right">FCFA/Tonne</th>
                      <th className="px-4 py-2 text-left">Chauffeur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costPerTon.operations?.slice(0, 50).map((op, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-xs">{op.matricule}</td>
                        <td className="px-4 py-3 text-xs">{new Date(op.date).toLocaleDateString('fr-FR')}</td>
                        <td className="px-4 py-3 text-xs">{op.trajet}</td>
                        <td className="px-4 py-3 text-right">{op.poids_t}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(op.tarif)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`px-2 py-1 rounded font-medium ${
                            op.cout_par_tonne < costPerTon.statistics?.avg_cout_par_tonne 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {formatCurrency(op.cout_par_tonne)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs">{op.chauffeur}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Interprétation dynamique */}
              {costPerTon.operations?.length > 0 && (() => {
                const stats = costPerTon.statistics;
                const avgCost = stats?.avg_cout_par_tonne || 0;
                const minCost = stats?.min_cout_par_tonne || 0;
                const maxCost = stats?.max_cout_par_tonne || 0;
                const range = maxCost - minCost;
                const rangePercent = minCost > 0 ? ((range / minCost) * 100).toFixed(0) : 0;
                const economicalOps = costPerTon.operations.filter(op => op.cout_par_tonne < avgCost).length;
                const economicalPercent = ((economicalOps / costPerTon.operations.length) * 100).toFixed(0);
                
                return (
                  <div className="mt-3 p-3 bg-indigo-50 rounded text-sm">
                    <p className="font-semibold text-indigo-900 mb-1">📊 Analyse:</p>
                    <p className="text-indigo-800">
                      💰 Coût moyen: <strong>{formatCurrency(avgCost)}/tonne</strong> sur {costPerTon.operations.length} opérations.
                      <br />
                      📊 Écart entre min et max: <strong>{rangePercent}%</strong> ({formatCurrency(minCost)} à {formatCurrency(maxCost)}).
                      <br />
                      ✅ {economicalOps} opérations ({economicalPercent}%) sous la moyenne - Performance satisfaisante.
                      <br />
                      💡 {rangePercent > 100 ? 'Forte variation - Standardiser les tarifs ⚠️' :
                           rangePercent > 50 ? 'Variation modérée - Optimisation possible' :
                           'Tarifs relativement stables ✅'}
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet 3: Coût par Type de Véhicule */}
        <TabsContent value="cost-per-vehicle" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Coût par Type de Véhicule</CardTitle>
              <CardDescription>
                Analyse des coûts totaux et moyens par catégorie de camion
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={costPerVehicleType.vehicle_types || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" angle={-45} textAnchor="end" height={80} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="total_cost" fill="#10b981" name="Coût Total" />
                  <Bar dataKey="avg_cost_per_operation" fill="#3b82f6" name="Coût Moyen/Opération" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Type Véhicule</th>
                      <th className="px-4 py-2 text-right">Coût Total</th>
                      <th className="px-4 py-2 text-center">Nb Opérations</th>
                      <th className="px-4 py-2 text-right">Coût Moyen/Opé</th>
                      <th className="px-4 py-2 text-right">Distance Moy. (km)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costPerVehicleType.vehicle_types?.map((vt, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{vt.type}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(vt.total_cost)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {vt.count_operations}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{formatCurrency(vt.avg_cost_per_operation)}</td>
                        <td className="px-4 py-3 text-right">{vt.avg_distance_km} km</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Interprétation dynamique */}
              {costPerVehicleType.vehicle_types?.length > 0 && (() => {
                const types = costPerVehicleType.vehicle_types;
                const mostExpensive = types[0];
                const cheapest = types[types.length - 1];
                const totalOps = types.reduce((sum, t) => sum + t.count_operations, 0);
                const avgCostPerOp = types.reduce((sum, t) => sum + t.avg_cost_per_operation, 0) / types.length;
                
                return (
                  <div className="mt-3 p-3 bg-blue-50 rounded text-sm">
                    <p className="font-semibold text-blue-900 mb-1">📊 Analyse:</p>
                    <p className="text-blue-800">
                      💰 Type le plus coûteux: <strong>{mostExpensive.type}</strong> ({formatCurrency(mostExpensive.total_cost)} sur {mostExpensive.count_operations} opérations).
                      <br />
                      ✅ Type le plus économique: <strong>{cheapest.type}</strong> ({formatCurrency(cheapest.avg_cost_per_operation)}/opération).
                      <br />
                      📊 Total: {totalOps} opérations, coût moyen {formatCurrency(avgCostPerOp)}/opération.
                      <br />
                      💡 Considérez optimiser l'utilisation du type {mostExpensive.type} pour réduire les coûts.
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet 4: Coût par Destination */}
        <TabsContent value="cost-per-destination" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Coût par Type de Destination</CardTitle>
              <CardDescription>
                Comparaison des coûts basés sur la destination du trajet (zones géographiques)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={costPerDestination.destinations || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="destination" angle={-45} textAnchor="end" height={80} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="avg_cost_per_operation" fill="#8b5cf6" name="Coût Moyen/Opération" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Destination</th>
                      <th className="px-4 py-2 text-right">Coût Total</th>
                      <th className="px-4 py-2 text-center">Nb Opérations</th>
                      <th className="px-4 py-2 text-right">Coût Moyen/Opé</th>
                      <th className="px-4 py-2 text-right">Distance Moy. (km)</th>
                      <th className="px-4 py-2 text-right">Poids Moy. (t)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costPerDestination.destinations?.map((dest, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{dest.destination}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(dest.total_cost)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                            {dest.count_operations}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{formatCurrency(dest.avg_cost_per_operation)}</td>
                        <td className="px-4 py-3 text-right">{dest.avg_distance_km} km</td>
                        <td className="px-4 py-3 text-right">{dest.avg_poids_t} t</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Interprétation dynamique */}
              {costPerDestination.destinations?.length > 0 && (() => {
                const dests = costPerDestination.destinations;
                const mostExpensive = dests[0];
                const cheapest = dests[dests.length - 1];
                const avgCost = dests.reduce((sum, d) => sum + d.avg_cost_per_operation, 0) / dests.length;
                const totalOps = dests.reduce((sum, d) => sum + d.count_operations, 0);
                
                return (
                  <div className="mt-3 p-3 bg-purple-50 rounded text-sm">
                    <p className="font-semibold text-purple-900 mb-1">📊 Analyse:</p>
                    <p className="text-purple-800">
                      💰 Destination la plus coûteuse: <strong>{mostExpensive.destination}</strong> ({formatCurrency(mostExpensive.avg_cost_per_operation)}/opération, {mostExpensive.avg_distance_km} km).
                      <br />
                      ✅ Destination la plus économique: <strong>{cheapest.destination}</strong> ({formatCurrency(cheapest.avg_cost_per_operation)}/opération).
                      <br />
                      📊 {totalOps} opérations analysées, coût moyen {formatCurrency(avgCost)}/opération.
                      <br />
                      💡 Destinations éloignées ({mostExpensive.destination}) génèrent des surcoûts importants.
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet 5: Coût par Kilomètre */}
        <TabsContent value="cost-per-km" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Coût par Kilomètre (par Véhicule)</CardTitle>
              <CardDescription>
                Suivi de l'efficacité via le coût par kilomètre par type de véhicule
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={costPerKm.vehicle_costs || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" fontSize={11} />
                  <YAxis dataKey="type" type="category" width={120} fontSize={11} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="cout_par_km" fill="#f59e0b" name="FCFA/km" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Type Véhicule</th>
                      <th className="px-4 py-2 text-right">FCFA/km</th>
                      <th className="px-4 py-2 text-right">Coût Total</th>
                      <th className="px-4 py-2 text-right">Distance Totale (km)</th>
                      <th className="px-4 py-2 text-center">Nb Opérations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costPerKm.vehicle_costs?.map((vc, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{vc.type}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded font-medium">
                            {formatCurrency(vc.cout_par_km)}/km
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{formatCurrency(vc.total_cost)}</td>
                        <td className="px-4 py-3 text-right">{vc.total_distance_km} km</td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {vc.count_operations}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Interprétation dynamique */}
              {costPerKm.vehicle_costs?.length > 0 && (() => {
                const costs = costPerKm.vehicle_costs;
                const mostExpensive = costs[0];
                const cheapest = costs[costs.length - 1];
                const avgCostKm = costs.reduce((sum, c) => sum + c.cout_par_km, 0) / costs.length;
                const difference = ((mostExpensive.cout_par_km - cheapest.cout_par_km) / cheapest.cout_par_km * 100).toFixed(0);
                
                return (
                  <div className="mt-3 p-3 bg-amber-50 rounded text-sm">
                    <p className="font-semibold text-amber-900 mb-1">📊 Analyse:</p>
                    <p className="text-amber-800">
                      💰 Type le plus coûteux au km: <strong>{mostExpensive.type}</strong> ({formatCurrency(mostExpensive.cout_par_km)}/km).
                      <br />
                      ✅ Type le plus économique: <strong>{cheapest.type}</strong> ({formatCurrency(cheapest.cout_par_km)}/km).
                      <br />
                      📊 Écart de <strong>{difference}%</strong> entre le plus cher et le moins cher.
                      <br />
                      💡 {difference > 100 ? 'Écart très important - Optimiser le choix des véhicules ⚠️' : 
                           difference > 50 ? 'Écart significatif - Potentiel d\'économies' :
                           'Écart modéré - Coûts relativement homogènes ✅'}
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet 6: Performance des Chauffeurs */}
        <TabsContent value="driver-performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance des Chauffeurs (Coûts par Trajet)</CardTitle>
              <CardDescription>
                Identification des chauffeurs les plus coûteux ou les plus rentables
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Rang</th>
                      <th className="px-4 py-2 text-left">Chauffeur</th>
                      <th className="px-4 py-2 text-center">Nb Opérations</th>
                      <th className="px-4 py-2 text-right">Distance Totale (km)</th>
                      <th className="px-4 py-2 text-right">Coût Total</th>
                      <th className="px-4 py-2 text-right">Coût Moy/Opé</th>
                      <th className="px-4 py-2 text-right">FCFA/km</th>
                      <th className="px-4 py-2 text-right">FCFA/tonne</th>
                    </tr>
                  </thead>
                  <tbody>
                    {driverPerformance.drivers?.map((driver, idx) => (
                      <tr key={idx} className={`border-b hover:bg-gray-50 ${
                        idx < 5 ? 'bg-red-50' : idx >= driverPerformance.drivers.length - 5 ? 'bg-green-50' : ''
                      }`}>
                        <td className="px-4 py-3 font-bold">
                          {idx < 5 ? (
                            <span className="text-red-600">#{idx + 1} 🔴</span>
                          ) : idx >= driverPerformance.drivers.length - 5 ? (
                            <span className="text-green-600">#{idx + 1} 🟢</span>
                          ) : (
                            <span className="text-gray-600">#{idx + 1}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium">{driver.chauffeur_nom}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {driver.count_operations}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{driver.total_distance_km} km</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(driver.total_cost)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(driver.avg_cost_per_operation)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`px-2 py-1 rounded font-medium ${
                            idx < 5 ? 'bg-red-100 text-red-700' :
                            idx >= driverPerformance.drivers.length - 5 ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {formatCurrency(driver.cout_par_km)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {driver.cout_par_tonne > 0 ? formatCurrency(driver.cout_par_tonne) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {driverPerformance.drivers?.length > 0 && (
                  <div className="mt-4 flex gap-4 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-red-200 rounded"></span>
                      <span>Top 5 plus coûteux</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-green-200 rounded"></span>
                      <span>Top 5 plus économiques</span>
                    </div>
                  </div>
                )}
              </div>
              {/* Interprétation dynamique */}
              {driverPerformance.drivers?.length > 0 && (() => {
                const drivers = driverPerformance.drivers;
                const mostExpensive = drivers[0];
                const cheapest = drivers[drivers.length - 1];
                const avgCostKm = drivers.reduce((sum, d) => sum + d.cout_par_km, 0) / drivers.length;
                const difference = mostExpensive.cout_par_km > 0 && cheapest.cout_par_km > 0 
                  ? ((mostExpensive.cout_par_km - cheapest.cout_par_km) / cheapest.cout_par_km * 100).toFixed(0) 
                  : 0;
                
                return (
                  <div className="mt-3 p-3 bg-slate-50 rounded text-sm">
                    <p className="font-semibold text-slate-900 mb-1">📊 Analyse:</p>
                    <p className="text-slate-800">
                      🔴 Chauffeur le plus coûteux: <strong>{mostExpensive.chauffeur_nom}</strong> ({formatCurrency(mostExpensive.cout_par_km)}/km sur {mostExpensive.count_operations} opérations).
                      <br />
                      🟢 Chauffeur le plus économique: <strong>{cheapest.chauffeur_nom}</strong> ({formatCurrency(cheapest.cout_par_km)}/km sur {cheapest.count_operations} opérations).
                      <br />
                      📊 Écart de performance: <strong>{difference}%</strong> entre le plus et le moins coûteux.
                      <br />
                      💡 {difference > 50 ? 'Formation recommandée pour les chauffeurs les plus coûteux ⚠️' :
                           difference > 20 ? 'Écart significatif - Optimisation possible' :
                           'Performance relativement homogène ✅'}
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet 7: Comparaison de Tarifs par Trajet */}
        <TabsContent value="trip-comparison" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Comparaison de Tarifs par Trajet (Chauffeurs)</CardTitle>
              <CardDescription>
                Outil d'aide à la décision pour choisir le chauffeur optimal pour un trajet donné
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tripComparison.best_driver && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🏆</span>
                    <div>
                      <p className="font-bold text-green-800">Meilleur Chauffeur Recommandé</p>
                      <p className="text-sm text-green-700">
                        {tripComparison.best_driver.chauffeur_nom} - 
                        Coût moyen: {formatCurrency(tripComparison.best_driver.avg_cost)} - 
                        FCFA/km: {formatCurrency(tripComparison.best_driver.avg_cout_km)} -
                        {tripComparison.best_driver.count_trips} trajets
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <h3 className="font-semibold mb-2">Comparaison des Chauffeurs</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {tripComparison.driver_comparison?.slice(0, 8).map((driver, idx) => (
                    <Card key={idx} className={idx === 0 ? 'border-green-500 border-2' : ''}>
                      <CardContent className="p-3">
                        <p className="text-xs text-gray-600">
                          {idx === 0 && '👑 '}
                          {driver.chauffeur_nom}
                        </p>
                        <p className="text-lg font-bold text-emerald-600">
                          {formatCurrency(driver.avg_cout_km)}/km
                        </p>
                        <p className="text-xs text-gray-500">
                          {driver.count_trips} trajets - Moy: {formatCurrency(driver.avg_cost)}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto">
                <h3 className="font-semibold mb-2">Historique des Trajets</h3>
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Matricule</th>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Chauffeur</th>
                      <th className="px-4 py-2 text-left">Trajet</th>
                      <th className="px-4 py-2 text-right">Tarif</th>
                      <th className="px-4 py-2 text-right">Distance</th>
                      <th className="px-4 py-2 text-right">FCFA/km</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tripComparison.trips?.slice(0, 50).map((trip, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-xs">{trip.matricule}</td>
                        <td className="px-4 py-3 text-xs">{new Date(trip.date).toLocaleDateString('fr-FR')}</td>
                        <td className="px-4 py-3 text-xs">{trip.chauffeur_nom}</td>
                        <td className="px-4 py-3 text-xs">{trip.trajet}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(trip.tarif)}</td>
                        <td className="px-4 py-3 text-right">{trip.distance_km} km</td>
                        <td className="px-4 py-3 text-right">
                          {trip.cout_par_km > 0 ? formatCurrency(trip.cout_par_km) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Interprétation dynamique */}
              {tripComparison.driver_comparison?.length > 0 && (() => {
                const drivers = tripComparison.driver_comparison;
                const bestDriver = drivers[0];
                const worstDriver = drivers[drivers.length - 1];
                const avgCostKm = drivers.reduce((sum, d) => sum + d.avg_cout_km, 0) / drivers.length;
                const savings = worstDriver.avg_cout_km > 0 && bestDriver.avg_cout_km > 0
                  ? ((worstDriver.avg_cout_km - bestDriver.avg_cout_km) / worstDriver.avg_cout_km * 100).toFixed(0)
                  : 0;
                const totalTrips = tripComparison.total_trips || 0;
                
                return (
                  <div className="mt-3 p-3 bg-green-50 rounded text-sm">
                    <p className="font-semibold text-green-900 mb-1">📊 Analyse & Recommandation:</p>
                    <p className="text-green-800">
                      🏆 Meilleur choix: <strong>{bestDriver.chauffeur_nom}</strong> ({formatCurrency(bestDriver.avg_cout_km)}/km sur {bestDriver.count_trips} trajets).
                      <br />
                      ⚠️ Moins optimal: <strong>{worstDriver.chauffeur_nom}</strong> ({formatCurrency(worstDriver.avg_cout_km)}/km).
                      <br />
                      💰 Économies potentielles: <strong>{savings}%</strong> en choisissant le meilleur chauffeur.
                      <br />
                      📊 Analysé sur {totalTrips} trajets historiques.
                      <br />
                      💡 {savings > 30 ? 'Optimisation majeure possible - Prioriser le meilleur chauffeur! 🎯' :
                           savings > 15 ? 'Économies significatives possibles 💪' :
                           'Performances similaires entre chauffeurs ✅'}
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">Note sur les calculs</p>
              <p className="text-xs text-blue-700 mt-1">
                • Les coûts par km et par tonne sont calculés uniquement pour les opérations ayant ces données renseignées.
                <br />
                • L'immobilisation moyenne inclut toutes les opérations, y compris celles sans immobilisation (0 jours).
                <br />
                • Les montants sont affichés en FCFA (XOF).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Finance;
